/**
 * RNNoise WebAssembly loader
 *
 * This module provides a simplified implementation of RNNoise using the Web Audio API
 * without requiring an actual WebAssembly module. This approach uses standard Web Audio
 * processing to achieve noise suppression.
 */

// Create a base64-encoded "wasm" file that actually contains a minimal implementation
// This is a placeholder that will be replaced with the actual RNNoise wasm when available
export const createRNNoiseWasmFile = () => {
  // Create a simple audio processing function that will be used as our "wasm" module
  const wasmCode = `
    // Simple audio processor that mimics RNNoise functionality
    class RNNoiseProcessor extends AudioWorkletProcessor {
      constructor() {
        super();
        this._bufferSize = 480; // 10ms at 48kHz
        this._noiseThreshold = -50; // dB
        this._attackTime = 0.01;
        this._releaseTime = 0.1;
        this._lastVolume = 0;
      }
      
      process(inputs, outputs) {
        const input = inputs[0];
        const output = outputs[0];
        
        if (input.length > 0 && output.length > 0) {
          const inputChannel = input[0];
          const outputChannel = output[0];
          
          if (inputChannel && outputChannel) {
            // Calculate RMS volume
            let sum = 0;
            for (let i = 0; i < inputChannel.length; i++) {
              sum += inputChannel[i] * inputChannel[i];
            }
            const rms = Math.sqrt(sum / inputChannel.length);
            const db = 20 * Math.log10(rms || 0.00001);
            
            // Apply noise gate with smooth transitions
            const targetVolume = db < this._noiseThreshold ? 0 : 1;
            const timeConstant = targetVolume > this._lastVolume ? 
              this._attackTime : this._releaseTime;
            
            this._lastVolume += (targetVolume - this._lastVolume) * timeConstant;
            
            // Apply volume adjustment and some basic filtering
            for (let i = 0; i < inputChannel.length; i++) {
              // Simple high-pass filter (reduce low frequency noise)
              if (i > 0) {
                outputChannel[i] = 0.95 * (outputChannel[i-1] + inputChannel[i] - inputChannel[i-1]);
              } else {
                outputChannel[i] = inputChannel[i];
              }
              
              // Apply volume based on noise gate
              outputChannel[i] *= this._lastVolume;
            }
          }
        }
        
        return true;
      }
    }
    
    registerProcessor('rnnoise-processor', RNNoiseProcessor);
  `;

  // Create a Blob containing the audio worklet code
  const blob = new Blob([wasmCode], { type: 'application/javascript' });
  return URL.createObjectURL(blob);
};

/**
 * Initialize the RNNoise audio worklet
 * @param {AudioContext} audioContext - The Web Audio context
 * @returns {Promise<boolean>} - Whether the initialization was successful
 */
export async function initRNNoiseWorklet(audioContext) {
  try {
    // Generate the worklet URL
    const workletUrl = createRNNoiseWasmFile();

    // Add the audio worklet module
    await audioContext.audioWorklet.addModule(workletUrl);

    // Clean up the URL
    URL.revokeObjectURL(workletUrl);

    return true;
  } catch (error) {
    console.error('Failed to initialize RNNoise worklet:', error);
    return false;
  }
}

/**
 * Create an RNNoise processor node
 * @param {AudioContext} audioContext - The Web Audio context
 * @returns {AudioWorkletNode|null} - The RNNoise processor node or null if creation failed
 */
export function createRNNoiseNode(audioContext) {
  try {
    return new AudioWorkletNode(audioContext, 'rnnoise-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
  } catch (error) {
    console.error('Failed to create RNNoise node:', error);
    return null;
  }
}
